// Weekly quests: three small goals per week (Monday–Sunday, local time), the same set for every
// learner that week (seeded by the week), with any quest that can't apply to this learner swapped
// for another (e.g. "close a Support Desk ticket" once the desk is finished).
//
// Progress is DERIVED, never stored: verified completions (src/core/weekly.js completions(), which
// reads the server-verified views with real accounts) and solved daily challenges. Quests give no
// XP (XP comes only from src/core/xp.js) and unlock nothing. The only stored state is `quests` in
// the learner store — which weeks were completed — so the celebration shows once and the Home
// card can show quest weeks and the weekly streak. It is cosmetic.
import { completions, weekStart, dayStart } from './weekly.js';
import { dayKey, solvedDays } from './daily.js';
import { SUPPORT_TICKETS } from '../domains/support/tickets.js';
import { missionKind } from '../map/mapData.js';

export const QUESTS_KEY = 'quests';
const DAY = 86400000;

/** Week id: the local date of its Monday, YYYY-MM-DD. */
export const weekKey = (t = Date.now()) => dayKey(weekStart(t));

const inWeek = (at, start) => Number.isFinite(at) && at >= start && at < start + 7 * DAY;
const labs = (c, start) => c.list.filter((x) => x.kind === 'lab' && inWeek(x.at, start));

/**
 * Quest pool. family: at most one quest per family in a week.
 * progress(ctx, weekStart) → number; feasible(ctx) → can this learner do it this week?
 */
export const QUEST_POOL = [
  { id: 'missions2', family: 'missions', icon: '🧭', title: 'Finish 2 missions', target: 2, progress: (c, s) => labs(c, s).length, feasible: (c) => c.openMissions >= 2 },
  { id: 'missions3', family: 'missions', icon: '🧭', title: 'Finish 3 missions', target: 3, progress: (c, s) => labs(c, s).length, feasible: (c) => c.openMissions >= 3 },
  { id: 'xp300', family: 'xp', icon: '⚡', title: 'Earn 300 XP', target: 300, unit: 'XP', progress: (c, s) => c.list.filter((x) => inWeek(x.at, s)).reduce((n, x) => n + (x.xp || 0), 0), feasible: (c) => c.openMissions >= 3 },
  { id: 'nohint', family: 'clean', icon: '🎯', title: 'Finish a mission without a hint', target: 1, progress: (c, s) => labs(c, s).filter((x) => !x.hints).length, feasible: (c) => c.openMissions >= 1 },
  { id: 'troubleshoot', family: 'trouble', icon: '🛠️', title: 'Solve a troubleshooting mission or final challenge', target: 1, progress: (c, s) => labs(c, s).filter((x) => x.missionKind === 'TROUBLESHOOTING' || x.missionKind === 'CHALLENGE').length, feasible: (c) => c.openTroubleshooting >= 1 },
  { id: 'daily3', family: 'daily', icon: '🔥', title: 'Solve the daily challenge on 3 days', target: 3, progress: (c, s) => c.daily.filter((d) => inWeek(d.at, s)).length, feasible: () => true },
  { id: 'daily5', family: 'daily', icon: '🔥', title: 'Solve the daily challenge on 5 days', target: 5, progress: (c, s) => c.daily.filter((d) => inWeek(d.at, s)).length, feasible: () => true },
  { id: 'firsttry2', family: 'sharp', icon: '💡', title: 'Get 2 daily challenges right on the first try', target: 2, progress: (c, s) => c.dailyFirst.filter((d) => inWeek(d.at, s)).length, feasible: () => true },
  { id: 'days3', family: 'days', icon: '📅', title: 'Practise on 3 different days', target: 3, progress: (c, s) => new Set([...c.list, ...c.daily].filter((x) => inWeek(x.at, s)).map((x) => dayStart(x.at))).size, feasible: () => true },
  { id: 'ticket', family: 'support', icon: '🎧', title: 'Close a Support Desk ticket', target: 1, progress: (c, s) => c.list.filter((x) => x.kind === 'ticket' && inWeek(x.at, s)).length, feasible: (c) => c.openTickets >= 1 },
];

function rng(seedText) {
  let a = 2166136261;
  for (const ch of seedText) a = Math.imul(a ^ ch.charCodeAt(0), 16777619);
  return () => { a |= 0; a = (a + 0x6D2B79F5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}

/**
 * Everything quests read, from verified records.
 * Open counts include items completed THIS week, so a week's quests don't change as you finish things.
 */
export function questContext({ progress, curriculum, support, daily, now = Date.now() }) {
  const list = completions({ progress, curriculum, support });
  const start = weekStart(now);
  const doneBefore = new Set(list.filter((x) => x.kind === 'lab' && x.at < start).map((x) => x.key));
  let openMissions = 0; let openTroubleshooting = 0;
  for (const d of curriculum.domains) {
    for (const u of d.units) {
      if (!u.implemented || doneBefore.has(`${d.id}.${u.id}`)) continue;
      openMissions++;
      if (['TROUBLESHOOTING', 'CHALLENGE'].includes(missionKind(u))) openTroubleshooting++;
    }
  }
  const ticketsBefore = list.filter((x) => x.kind === 'ticket' && x.at < start).length;
  const dailyFirst = Object.values(daily && typeof daily === 'object' ? daily : {}).filter((v) => v && v.done && v.firstTry && Number.isFinite(v.at)).map((v) => ({ at: v.at }));
  return { list, daily: solvedDays(daily), dailyFirst, openMissions, openTroubleshooting, openTickets: SUPPORT_TICKETS.length - ticketsBefore };
}

/** This week's three quests (seeded by the week, filtered to what this learner can do). */
export function weekQuests(ctx, key) {
  const r = rng(`routezero-quests-${key}`);
  const order = QUEST_POOL.map((q) => [r(), q]).sort((a, b) => a[0] - b[0]).map(([, q]) => q);
  const picked = [];
  for (const q of order) {
    if (picked.length === 3) break;
    if (picked.some((p) => p.family === q.family) || !q.feasible(ctx)) continue;
    picked.push(q);
  }
  return picked;
}

/**
 * The quest board for the week containing `now`.
 * @returns {{ key:string, start:number, end:number, daysLeft:number, quests:{id,icon,title,target,unit?,value,done}[], complete:boolean }}
 */
export function questBoard({ progress, curriculum, support, daily, now = Date.now() }) {
  const ctx = questContext({ progress, curriculum, support, daily, now });
  const start = weekStart(now);
  const key = dayKey(start);
  const quests = weekQuests(ctx, key).map((q) => {
    const value = Math.min(q.target, q.progress(ctx, start));
    return { id: q.id, icon: q.icon, title: q.title, target: q.target, unit: q.unit || '', value, done: value >= q.target };
  });
  const end = start + 7 * DAY;
  return { key, start, end, daysLeft: Math.max(1, Math.ceil((end - now) / DAY)), quests, complete: quests.length > 0 && quests.every((q) => q.done) };
}

/** Record a completed week (pure over the stored object). */
export const recordWeek = (data, key, at = Date.now()) => ({ ...(data && typeof data === 'object' ? data : {}), [key]: (data && data[key]) || { at } });

/** Completed weeks and the current streak of consecutive completed weeks (ending this week or last). */
export function questStats(data, now = Date.now()) {
  const weeks = new Set(Object.keys(data && typeof data === 'object' ? data : {}).filter((k) => /^\d{4}-\d{2}-\d{2}$/.test(k)));
  let streak = 0;
  let t = weekStart(now);
  if (!weeks.has(dayKey(t))) t = weekStart(t - DAY);
  while (weeks.has(dayKey(t))) { streak++; t = weekStart(t - DAY); }
  return { weeks: weeks.size, streak };
}
